import React, { useState } from "react";

import type * as ozon from "@/api/ozonSupply";
import { cn } from "@/lib/utils";
import { offersAreLive } from "./dropOff";
import { fmt, int0, plural } from "../format";

/** Выбор склада прямой поставки — ИЗ ТЕХ, что предложила площадка.
 *
 *  ⚠️ Раньше склад выбирал портал: брал тот, что возьмёт больше всего. Но склады кластера не
 *  равнозначны — до одного логистика есть, до другого нет, — а портал знает только числа
 *  площадки. Теперь решает человек, и решает ГЛЯДЯ НА ЧИСЛА: у каждого склада написано, сколько
 *  он заберёт и сколько отсечёт.
 *
 *  Переключение не тратит черновик: числа по всем складам кластера площадка отдала разом.
 */
export function WarehousePicker({
  d,
  disabled,
  onPick,
}: {
  d: ozon.OzonDirection;
  disabled: boolean;
  onPick: (shipmentId: number, warehouseId: number | null) => void;
}): React.ReactElement | null {
  const [compare, setCompare] = useState(false);
  const live = d.offers.filter((o) => o.available);
  // у кросс-дока склада хранения нет — его назначает площадка, выбирать нечего
  if (d.kind !== "direct") {
    return d.warehouse ? (
      <span className="text-xs text-muted-foreground">
        склад: {d.warehouse}
      </span>
    ) : (
      <span className="text-xs text-muted-foreground/70">
        склад назначит площадка
      </span>
    );
  }
  // ⚠️ Список складов приходит ВМЕСТЕ с черновиком. У поставок, снятых до появления выбора, его
  // просто нет — и молчаливое «склад: ГРИВНО_РФЦ» читается как «выбирать нельзя». Говорим, что
  // делать: спросить площадку заново.
  if (live.length === 0) {
    return (
      <span className="text-xs text-muted-foreground">
        {d.warehouse ? `склад: ${d.warehouse}` : "склад — после черновика"}
        {d.state !== "" && (
          <span className="ml-1 text-amber-600 dark:text-amber-400">
            · спросите площадку заново, чтобы выбрать склад
          </span>
        )}
      </span>
    );
  }
  // после заявки склад уже не меняют: она живёт в кабинете со своим складом и окном
  if (d.order_id) {
    return (
      <span className="text-xs text-muted-foreground">
        склад: {d.warehouse}
      </span>
    );
  }
  // все позиции, которые хоть один склад отсекает: по ним и сравнивают
  const cut = [
    ...new Map(
      live.flatMap((o) => o.cuts).map((c) => [c.mp_sku, c.article]),
    ).entries(),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  return (
    <div className="flex flex-col gap-1">
      <label className="flex items-center gap-1 text-xs text-muted-foreground">
        склад:
        <select
          value={d.warehouse_pick ?? ""}
          disabled={disabled}
          onChange={(e) =>
            onPick(
              d.shipment_id,
              e.target.value ? Number(e.target.value) : null,
            )
          }
          className="h-7 rounded border border-input bg-background px-1 text-xs"
          title="Куда везём. У каждого склада — сколько он заберёт из этого состава и сколько отсечёт"
        >
          <option value="">по умолчанию (заберёт больше всех)</option>
          {live.map((o) => (
            <option key={o.warehouse_id} value={o.warehouse_id}>
              {o.name} — возьмёт {int0(o.accepted)}
              {o.dropped > 0 ? `, отсечёт ${int0(o.dropped)}` : ""}
              {o.volume_l != null && offersAreLive(d) ? ` · ${fmt(o.volume_l)} л по Ozon` : ""}
            </option>
          ))}
        </select>
        {d.warehouse && (
          <span className="text-muted-foreground/70">→ {d.warehouse}</span>
        )}
        {/* ⚠️ Отказавшие склады НАЗЫВАЕМ числом: иначе «в списке два варианта» читается как
          поломка портала, а это ответ площадки — она режет по рангу и по товарной матрице */}
        {d.offers.length > live.length && (
          <span
            className="text-muted-foreground/70"
            title={d.offers
              .filter((o) => !o.available)
              .map((o) => `${o.name}: ${o.reason || o.status}`)
              .join("\n")}
          >
            · {d.offers.length - live.length}{" "}
            {plural(
              d.offers.length - live.length,
              "склад",
              "склада",
              "складов",
            )}{" "}
            площадка сюда не везёт
          </span>
        )}
        {live.some((o) => o.dropped > 0) && (
          <button
            type="button"
            className="rounded px-1 text-muted-foreground underline decoration-dotted hover:bg-accent"
            onClick={() => setCompare((v) => !v)}
          >
            {compare ? "скрыть" : "что отсекут"}
          </button>
        )}
      </label>
      {/* ⚠️ Сравнение ПОСТРОЧНОЕ, а не «отсечёт 200». Разница между складами часто в одной-двух
          позициях, и тогда дешевле убрать позицию, чем везти за 200 км в другой склад. Ответить
          на это может только список: что именно теряем у каждого. */}
      {compare && cut.length > 0 && (
        <table className="w-fit border-collapse text-xs">
          <thead className="text-muted-foreground">
            <tr className="border-b text-left">
              <th className="py-0.5 pr-3 font-normal">Артикул</th>
              {live.map((o) => (
                <th
                  key={o.warehouse_id}
                  className="py-0.5 pl-3 text-right font-normal"
                >
                  {o.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cut.map(([sku, article]) => (
              <tr key={sku} className="border-b last:border-0">
                <td className="py-0.5 pr-3">{article}</td>
                {live.map((o) => {
                  const lost =
                    o.cuts.find((c) => c.mp_sku === sku)?.dropped ?? 0;
                  return (
                    <td
                      key={o.warehouse_id}
                      className={cn(
                        "py-0.5 pl-3 text-right tabular-nums",
                        lost > 0
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-muted-foreground/60",
                      )}
                    >
                      {lost > 0 ? `−${int0(lost)}` : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="font-medium">
              <td className="py-0.5 pr-3 text-muted-foreground">
                всего отсекут
              </td>
              {live.map((o) => (
                <td
                  key={o.warehouse_id}
                  className="py-0.5 pl-3 text-right tabular-nums"
                >
                  {o.dropped > 0 ? `−${int0(o.dropped)}` : "—"}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}
